import {
  ExportRemittanceWorksParams,
  FileExtensionReference,
} from '@kaa/api/providers';
import { httpTo } from '@kaa/api/providers/utilities';
import { useAsyncCallback } from '@kaa/common/utils';
import { DownloadTemplates } from '@kaa/core-app/providers/components';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import {
  SwFetchErrorMessage,
  SwLoader,
  SwModalRenderProps,
  SwTitle,
} from '@kaa/ui-flanders/components';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../../../utils';
import { getApplicationType } from '../../../../../utils/templates';

type RemittanceManualCreationDownloadModal = {
  remittanceId: number;
  providerId: number;
  parameters?: ExportRemittanceWorksParams;
};

export const RemittanceManualCreationDownloadModal = ({
  remittanceId,
  providerId,
  parameters,
}: SwModalRenderProps<RemittanceManualCreationDownloadModal>) => {
  const { t } = useTranslation();
  const { providers } = useApi();

  const [
    { value: templateLink, loading, error },
    getRemittancesExport,
  ] = useAsyncCallback(
    async () => {
      const [, payload] = await httpTo(
        providers.exportRemittanceWorks(
          providerId,
          remittanceId,
          parameters as ExportRemittanceWorksParams,
        ),
      );

      if (!payload) {
        return undefined;
      }

      const blob = new Blob([payload.data as BlobPart], {
        type: getApplicationType(
          parameters?.fileExtension || FileExtensionReference.XLSX,
        ),
      });

      const url = window.URL.createObjectURL(blob);

      return { url, blob };
    },
    [providers],
    { loading: true },
  );

  useEffect(() => {
    getRemittancesExport();
  }, [getRemittancesExport]);

  if (loading) {
    return <SwLoader />;
  }

  if (error || !templateLink) {
    return <SwFetchErrorMessage onClick={getRemittancesExport} />;
  }

  return (
    <>
      <SwTitle tagName="h2" className="vl-u-spacer--medium">
        {t(i18nKeys.remittances.modal.exportRemittanceWorks.title)}
      </SwTitle>
      <DownloadTemplates
        linkToTemplate={templateLink.url}
        fileName={`${remittanceId}`}
        blob={templateLink.blob}
      />
    </>
  );
};
